import {
    getInvoiceItem,
    listInvoiceItems
} from './queries';
import {
    createInvoiceItem,
    deleteInvoiceItem,
    updateInvoiceItem,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';
import {InvoiceItem} from "../types.ts";
import {generateClient} from "aws-amplify/api";


export type ListInvoiceItems = {
    listInvoiceItems: { items: InvoiceItem[], nextToken?: string }
}

export type GetInvoiceItem = {
    getInvoiceItem: InvoiceItem
}

export type CreateInvoiceItem = {
    createInvoiceItem: InvoiceItem
}

export type UpdateInvoiceItem = {
    updateInvoiceItem: InvoiceItem
}

export type DeleteInvoiceItem = {
    deleteInvoiceItem: string
}

const client = generateClient();

export const fetchRecords = async (filter: object, limit?: number, nextToken?: string) => {

    const response = await client.graphql<GraphQLQuery<ListInvoiceItems>>({
                                                                              query: listInvoiceItems,
                                                                              variables: {filter: filter, limit, nextToken},
                                                                          });
    return response.data?.listInvoiceItems;
};

export const fetch = async (invoiceId: string, id: string) => {
    const response = await client.graphql<GraphQLQuery<GetInvoiceItem>>({
                                                                            query: getInvoiceItem,
                                                                            variables: {
                                                                                invoice: invoiceId,
                                                                                id: id
                                                                            },
                                                                        });
    return response.data?.getInvoiceItem;
};

/**
 * FNP-918: payloads are WHITELISTED to the API's Input type (§6).
 *
 * ⚠️ The previous shape built the payload by `delete`-ing keys off the record
 * — and `update` did it to the CALLER'S OBJECT, not a copy. That mutates the
 * live Formik values / React Query cache entry the caller still holds, so a
 * failed mutation left the form missing fields it had a moment earlier.
 *
 * Deleting is also the wrong direction: it removes the keys you remembered to
 * name and sends everything you did not. `dateAtMillis` (a client-side derived
 * field) and `date` itself are not on either Input, so a spread would have the
 * API reject the whole mutation.
 *
 * Undefined entries are dropped so an untouched optional field is omitted
 * rather than sent as an explicit null.
 */
const pick = <T extends object>(source: T, keys: (keyof T)[]): Record<string, unknown> =>
    keys.reduce<Record<string, unknown>>((acc, key) => {
        const value = source[key];
        if (typeof value !== 'undefined') {
            acc[key as string] = value;
        }
        return acc;
    }, {});

/** Exactly the fields CreateInvoiceItemInput declares. */
const CREATE_FIELDS: (keyof Omit<InvoiceItem, 'id'>)[] = [
    'amount',
    'currency',
    'customerId',
    'description',
    'invoice',
    'period',
    'price',
    'quantity',
    'subscription',
    'unitAmount',
];

/**
 * Exactly the fields UpdateInvoiceItemInput declares.
 *
 * ⚠️ Narrower than create: the API accepts NO `customerId`, `currency`,
 * `invoice` or `subscription` on update — an invoice item cannot be moved
 * between customers or invoices. The old code sent them anyway.
 */
const UPDATE_FIELDS: (keyof InvoiceItem)[] = [
    'id',
    'amount',
    'description',
    'period',
    'price',
    'quantity',
    'unitAmount',
];

// A create has no id yet — the API assigns it — so the input is the record
// MINUS its id. Requiring a full InvoiceItem here forced callers to invent one.
export const add = async (payload: Omit<InvoiceItem, 'id'>) => {

    const response = await client.graphql<GraphQLQuery<CreateInvoiceItem>>({
                                                                               query: createInvoiceItem,
                                                                               variables: {input: pick(payload, CREATE_FIELDS)},
                                                                           });

    return response.data?.createInvoiceItem;
};

export const update = async (record: InvoiceItem) => {

    const response = await client.graphql<GraphQLQuery<UpdateInvoiceItem>>({
                                                                               query: (updateInvoiceItem),
                                                                               variables: {input: pick(record, UPDATE_FIELDS)},
                                                                           });
    return response.data?.updateInvoiceItem;
};

export const deleteRecord = async (_invoiceId: string, id: string) => {
    const response = await client.graphql<GraphQLQuery<DeleteInvoiceItem>>({
                                                                               query: (deleteInvoiceItem),
                                                                               variables: {
                                                                                   input: {
                                                                                       id: id
                                                                                   }
                                                                               },

                                                                           });
    return response.data?.deleteInvoiceItem;
};
